import { useState } from "react";
import { ChevronDown, ShieldCheck } from "lucide-react";

const FAQ_ITEMS = [
  {
    q: "Does an IMCAC/DOOSP certificate replace my professional license?",
    a: "No. IMCAC and its department, the Department of Occupational Safety Programs (DOOSP), is an independent competency assessment and certification body. Our certificate is a competency-based credential — it does not replace, substitute, or satisfy any professional registration, licensing, or regulatory requirements imposed by national authorities, medical councils, nursing boards, or any other statutory body. If your role legally requires a specific license or registration, you must obtain that separately from the relevant regulator.",
  },
  {
    q: "Is IMCAC accredited by, or affiliated with, OSHA, WHO, CDC, or any government?",
    a: "No. IMCAC is not accredited by, endorsed by, or affiliated with OSHA, WHO, the CDC, or any government or regulatory body. Our curriculum is aligned to the topics covered by OSHA 29 CFR 1910.1030 and to guidance published by the CDC and WHO — meaning the content of our assessments draws from these public standards. Alignment reflects content coverage; it is not accreditation, endorsement, or recognition.",
  },
  {
    q: "What is IMCAC, exactly?",
    a: "IMCAC — the International Medical Competency Accreditation Council — is a registered Section 8 company under the Companies Act, 2013, India, operating as a not-for-profit organisation. We design and administer competency-based assessments for medical, health, and workplace-safety professionals. We are not a licensing authority or regulator.",
  },
  {
    q: "What does an IMCAC certificate actually prove?",
    a: "It proves that the named holder successfully completed a competency-based assessment on the topics indicated — for example, Bloodborne Pathogens aligned to OSHA 29 CFR 1910.1030. It is verifiable via a public certificate ID and QR code, and it is valid for two calendar years from the date of issue.",
  },
  {
    q: "Why is there a fee for the certificate?",
    a: "Enrollment and learning are free. The one-time fee ($30 for individual certificates, discounted for corporate bulk orders) covers competency assessment, AI-graded case-study review, secure PDF generation, and hosted verification for the two-year validity period. IMCAC is a Section-8 non-profit; fees fund program operations, not distributions.",
  },
  {
    q: "Will my employer accept this certificate?",
    a: "Many employers accept our competency-based certificate as evidence that an employee has been trained on the topics covered — commonly required under OSHA 29 CFR 1910.1030 for bloodborne pathogens. However, employers make their own acceptance decisions, and some may require licensure or a specific accredited provider. Confirm your employer's requirements before enrolling.",
  },
];

export default function HomeFAQ() {
  const [openIdx, setOpenIdx] = useState(0);

  // schema.org FAQPage — helps search engines correctly index our positioning.
  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQ_ITEMS.map((it) => ({
      "@type": "Question",
      name: it.q,
      acceptedAnswer: { "@type": "Answer", text: it.a },
    })),
  };

  return (
    <section className="border-b border-zinc-200 bg-zinc-50" data-testid="home-faq-section">
      {/* SEO structured data */}
      <script
        type="application/ld+json"
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqJsonLd) }}
      />

      <div className="max-w-5xl mx-auto px-6 py-20 lg:py-24">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
          <div>
            <div className="text-xs font-mono uppercase tracking-widest text-red-800 mb-3 flex items-center gap-2">
              <ShieldCheck className="h-3.5 w-3.5" /> Frequently asked · positioning
            </div>
            <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight leading-tight">
              What our certificate<br />
              is — and isn&rsquo;t.
            </h2>
          </div>
          <p className="text-sm text-zinc-600 max-w-md leading-relaxed">
            We&rsquo;re direct about scope so there are no surprises later. IMCAC is an{" "}
            <strong>independent competency assessment and certification body</strong> — not an
            accreditor, licensor, or regulator.
          </p>
        </div>

        <div className="border-t border-zinc-200">
          {FAQ_ITEMS.map((it, i) => {
            const open = openIdx === i;
            return (
              <div key={it.q} className="border-b border-zinc-200">
                <button
                  onClick={() => setOpenIdx(open ? -1 : i)}
                  className="w-full text-left py-5 flex items-start justify-between gap-6 group"
                  aria-expanded={open}
                  data-testid={`faq-item-${i}`}
                >
                  <div className="flex-1">
                    <div className="text-[11px] font-mono uppercase tracking-widest text-zinc-400 mb-1">
                      Q{i + 1}
                    </div>
                    <div className="font-display font-semibold text-lg text-zinc-900 group-hover:text-red-800 transition-colors">
                      {it.q}
                    </div>
                  </div>
                  <ChevronDown
                    className={`h-5 w-5 text-zinc-400 mt-3 flex-shrink-0 transition-transform ${open ? "rotate-180 text-red-800" : ""}`}
                  />
                </button>
                {open && (
                  <div className="pb-6 pr-10 text-zinc-700 leading-relaxed max-w-3xl" data-testid={`faq-answer-${i}`}>
                    {it.a}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
